import { useEffect, useState, useMemo, useRef } from "react";
import AppLayout from "@/components/AppLayout";
import { api, formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import AdminDeleteConfirmation from "@/components/AdminDeleteConfirmation";
import OnlineRatings from "@/components/OnlineRatings";
import { Pencil, Trash2, ClipboardList, Plus, Search, FileUp, FileDown, Video as VideoIcon, Camera, Eye } from "lucide-react";

const VIDEO_CATEGORIES = ["Táticas", "Aberturas", "Meio-Jogo", "Finais", "Estratégia", "Análise de Partida", "Outros"];

const newAthleteEmpty = {
  name: "", email: "", password: "gsx2026", category: "Sub-14",
  class_name: "",
  school: "", city: "", birth_date: "",
  cpf: "", rg: "", additional_info: "",
  rating_cbx: 0, rating_fide: 0, has_rating_cbx: false, has_rating_fide: false,
  chess_com_username: "", lichess_username: "", fsx_player_id: "",
};

const emptyVideo = { title: "", youtube_url: "", description: "", category: "Táticas" };

function avatarSrc(a) {
  if (!a?.avatar_file_id) return null;
  return `${API}/athletes/${a.id}/avatar?v=${encodeURIComponent(a.avatar_uploaded_at || a.avatar_file_id)}`;
}

async function openPdfInNewTab(athleteId) {
  try {
    const res = await fetch(`${API}/athletes/${athleteId}/plan/download`, {
      credentials: "include",
    });
    if (!res.ok) throw new Error("fail");
    const blob = await res.blob();
    window.open(URL.createObjectURL(blob), "_blank");
  } catch {
    toast.error("Não foi possível abrir o PDF");
  }
}

export default function AdminAthletes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(newAthleteEmpty);
  const [creating, setCreating] = useState(false);

  const [editAthlete, setEditAthlete] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editing, setEditing] = useState(false);
  const [videos, setVideos] = useState([]);
  const [videoForm, setVideoForm] = useState(emptyVideo);
  const [editingVideoId, setEditingVideoId] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const pdfInputRef = useRef(null);
  const avatarInputRef = useRef(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get("/athletes");
      setItems(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Falha ao carregar atletas");
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const s = q.toLowerCase();
    return items.filter(a => (a.name||"").toLowerCase().includes(s)
      || (a.email||"").toLowerCase().includes(s)
      || (a.category||"").toLowerCase().includes(s));
  }, [items, q]);

  const setCF = (k, v) => setCreateForm(f => ({ ...f, [k]: v }));
  const setEF = (k, v) => setEditForm(f => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault(); setCreating(true);
    if (!/^\d{11}$/.test(createForm.cpf || "")) {
      toast.error("Informe um CPF com exatamente 11 números");
      setCreating(false);
      return;
    }
    const fsxId = createForm.fsx_player_id.trim() ? Number(createForm.fsx_player_id) : null;
    if (fsxId !== null && (!Number.isInteger(fsxId) || fsxId <= 0)) {
      toast.error("O ID FSX deve ser um nÃºmero inteiro maior que zero");
      setCreating(false);
      return;
    }
    try {
      await api.post("/athletes", {
        ...createForm,
        rating_cbx: createForm.has_rating_cbx ? Number(createForm.rating_cbx)||0 : 0,
        rating_fide: createForm.has_rating_fide ? Number(createForm.rating_fide)||0 : 0,
        chess_com_username: createForm.chess_com_username.trim() || null,
        lichess_username: createForm.lichess_username.trim() || null,
        fsx_player_id: fsxId,
      });
      toast.success("Atleta cadastrado. Senha inicial: " + createForm.password);
      setCreateForm(newAthleteEmpty); setCreateOpen(false);
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  const openEdit = async (a) => {
    setEditAthlete(a);
    setEditForm({
      name: a.name||"", email: a.email||"", password: "",
      birth_date: a.birth_date||"", category: a.category||"",
      class_name: a.class_name||"",
      school: a.school||"", city: a.city||"",
      cpf: a.cpf||"", rg: a.rg||"", additional_info: a.additional_info||"",
      rating_cbx: a.rating_cbx||0, rating_fide: a.rating_fide||0,
      has_rating_cbx: Number(a.rating_cbx) > 0, has_rating_fide: Number(a.rating_fide) > 0,
      chess_com_username: a.chess_com_username||"", lichess_username: a.lichess_username||"",
      fsx_player_id: a.fsx_player_id == null ? "" : String(a.fsx_player_id),
    });
    setVideos([]); setVideoForm(emptyVideo); setEditingVideoId(null);
    try {
      const { data } = await api.get(`/athletes/${a.id}/videos`);
      setVideos(data);
    } catch (err) {
      console.error("[admin] falha ao carregar vídeos do atleta:", err);
      toast.error("Não foi possível carregar os vídeos deste atleta.");
    }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editAthlete) return;
    setEditing(true);
    try {
      const payload = { ...editForm };
      if (!payload.password) delete payload.password;
      payload.rating_cbx = payload.has_rating_cbx ? Number(payload.rating_cbx)||0 : 0;
      payload.rating_fide = payload.has_rating_fide ? Number(payload.rating_fide)||0 : 0;
      delete payload.has_rating_cbx;
      delete payload.has_rating_fide;
      payload.chess_com_username = (payload.chess_com_username || "").trim() || null;
      payload.lichess_username = (payload.lichess_username || "").trim() || null;
      const fsxId = (payload.fsx_player_id || "").trim() ? Number(payload.fsx_player_id) : null;
      if (fsxId !== null && (!Number.isInteger(fsxId) || fsxId <= 0)) {
        toast.error("O ID FSX deve ser um nÃºmero inteiro maior que zero");
        setEditing(false);
        return;
      }
      payload.fsx_player_id = fsxId;
      const { data } = await api.put(`/athletes/${editAthlete.id}`, payload);
      setItems(prev => prev.map(a => a.id === editAthlete.id ? { ...a, ...data } : a));
      setEditAthlete(prev => prev ? { ...prev, ...data } : prev);
      setEditForm(prev => ({ ...prev, chess_com_username: data.chess_com_username || "", lichess_username: data.lichess_username || "", fsx_player_id: data.fsx_player_id == null ? "" : String(data.fsx_player_id) }));
      toast.success("Atleta atualizado");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setEditing(false); }
  };

  const remove = async (a) => {
    try {
      await api.delete(`/athletes/${a.id}`);
      setItems(prev => prev.filter(x => x.id !== a.id));
      toast.success("Atleta removido");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  // ----- Plan PDF -----
  const uploadPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editAthlete) return;
    setUploadingPdf(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/athletes/${editAthlete.id}/plan/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      const updated = { ...editAthlete, plan_pdf_name: data.plan_pdf_name };
      setEditAthlete(updated);
      setItems(prev => prev.map(a => a.id === updated.id ? updated : a));
      toast.success("Plano publicado");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Falha no envio"); }
    finally { setUploadingPdf(false); if (pdfInputRef.current) pdfInputRef.current.value = ""; }
  };

  const deletePdf = async () => {
    if (!editAthlete || !window.confirm("Remover o plano atual?")) return;
    try {
      await api.delete(`/athletes/${editAthlete.id}/plan`);
      const updated = { ...editAthlete }; delete updated.plan_pdf_name;
      setEditAthlete(updated);
      setItems(prev => prev.map(a => a.id === updated.id ? updated : a));
      toast.success("Plano removido");
    } catch { toast.error("Erro"); }
  };

  // ----- Avatar -----
  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editAthlete) return;
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/athletes/${editAthlete.id}/avatar`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      const updated = { ...editAthlete, avatar_file_id: data.avatar_file_id, avatar_uploaded_at: new Date().toISOString() };
      setEditAthlete(updated);
      setItems(prev => prev.map(a => a.id === updated.id ? updated : a));
      toast.success("Foto publicada");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Falha no envio da foto"); }
    finally { setUploadingAvatar(false); if (avatarInputRef.current) avatarInputRef.current.value = ""; }
  };

  const deleteAvatar = async () => {
    if (!editAthlete || !window.confirm("Remover a foto atual?")) return;
    try {
      await api.delete(`/athletes/${editAthlete.id}/avatar`);
      const updated = { ...editAthlete }; delete updated.avatar_file_id; delete updated.avatar_uploaded_at;
      setEditAthlete(updated);
      setItems(prev => prev.map(a => a.id === updated.id ? updated : a));
      toast.success("Foto removida");
    } catch { toast.error("Erro"); }
  };

  // ----- Videos -----
  const saveVideo = async (e) => {
    e.preventDefault();
    if (!editAthlete) return;
    try {
      const u = new URL(videoForm.youtube_url);
      const host = u.hostname.replace(/^www\./, "");
      const id = host === "youtu.be" ? u.pathname.split("/")[1] : (u.searchParams.get("v") || u.pathname.split("/")[2]);
      if (!['youtube.com', 'm.youtube.com', 'youtu.be'].includes(host) || !/^[A-Za-z0-9_-]{11}$/.test(id || "")) throw new Error();
    } catch { toast.error("Informe uma URL válida de vídeo do YouTube"); return; }
    try {
      if (editingVideoId) {
        const { data } = await api.put(`/athletes/${editAthlete.id}/videos/${editingVideoId}`, videoForm);
        setVideos(prev => prev.map(v => v.id === editingVideoId ? data : v));
        toast.success("Vídeo atualizado");
      } else {
        const { data } = await api.post(`/athletes/${editAthlete.id}/videos`, videoForm);
        setVideos(prev => [data, ...prev]);
        toast.success("Vídeo adicionado");
      }
      setVideoForm(emptyVideo); setEditingVideoId(null);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const editVideo = (v) => { setEditingVideoId(v.id); setVideoForm({
    title: v.title, youtube_url: v.youtube_url, description: v.description||"",
    category: v.category || "Táticas",
  }); };

  const removeVideo = async (v) => {
    if (!editAthlete || !window.confirm("Remover vídeo?")) return;
    try {
      await api.delete(`/athletes/${editAthlete.id}/videos/${v.id}`);
      setVideos(prev => prev.filter(x => x.id !== v.id));
    } catch { toast.error("Erro"); }
  };

  return (
    <AppLayout kind="admin">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl text-white font-semibold">Gestão de Atletas</h1>
          <p className="text-slate-400 mt-1">Cadastro, edição, plano em PDF, foto e vídeos individuais.</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Buscar…"
              data-testid="admin-athletes-search"
              className="pl-9 bg-[#0D2A52] border-amber-500/20 text-white w-56" />
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="admin-add-athlete-btn" className="bg-amber-500 text-[#071A33] hover:bg-amber-400 font-semibold">
                <Plus className="w-4 h-4 mr-1" /> Novo Atleta
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#071A33] border-amber-500/30 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Novo Atleta</DialogTitle></DialogHeader>
              <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
                <F label="Nome completo" v={createForm.name} set={(v)=>setCF("name",v)} testid="new-athlete-name" required />
                <F label="Email" type="email" v={createForm.email} set={(v)=>setCF("email",v)} testid="new-athlete-email" required />
                <F label="Senha inicial" v={createForm.password} set={(v)=>setCF("password",v)} required />
                <F label="Data de nascimento" type="date" v={createForm.birth_date} set={(v)=>setCF("birth_date",v)} />
                <F label="Categoria" v={createForm.category} set={(v)=>setCF("category",v)} />
                <F label="Turma (ex: Terça 19h)" v={createForm.class_name} set={(v)=>setCF("class_name",v)} testid="new-athlete-class" />
                <F label="Escola" v={createForm.school} set={(v)=>setCF("school",v)} />
                <F label="Cidade" v={createForm.city} set={(v)=>setCF("city",v)} />
                <F label="CPF *" type="tel" v={createForm.cpf} set={(v)=>setCF("cpf",v.replace(/\D/g, "").slice(0, 11))} testid="new-athlete-cpf" required minLength={11} maxLength={11} pattern="[0-9]{11}" />
                <F label="RG" v={createForm.rg} set={(v)=>setCF("rg",v)} testid="new-athlete-rg" />
                <RatingEditor label="CBX" checked={createForm.has_rating_cbx} onCheckedChange={v=>setCF("has_rating_cbx",v)} value={createForm.rating_cbx} onValueChange={v=>setCF("rating_cbx",v)} testid="new-athlete-rating-cbx" />
                <RatingEditor label="FIDE" checked={createForm.has_rating_fide} onCheckedChange={v=>setCF("has_rating_fide",v)} value={createForm.rating_fide} onValueChange={v=>setCF("rating_fide",v)} testid="new-athlete-rating-fide" />
                <F label="Username Chess.com" v={createForm.chess_com_username} set={(v)=>setCF("chess_com_username",v)} />
                <F label="Username Lichess" v={createForm.lichess_username} set={(v)=>setCF("lichess_username",v)} />
                <F label="ID FSX" type="number" v={createForm.fsx_player_id} set={(v)=>setCF("fsx_player_id",v)} />
                <div className="sm:col-span-2 space-y-2">
                  <Label className="text-slate-300">Informações adicionais</Label>
                  <Textarea rows={3} value={createForm.additional_info}
                    onChange={(e)=>setCF("additional_info", e.target.value)}
                    data-testid="new-athlete-additional-info"
                    className="bg-[#0D2A52] border-amber-500/20 text-white" />
                </div>
                <Button type="submit" disabled={creating} data-testid="new-athlete-save"
                  className="sm:col-span-2 bg-amber-500 text-[#071A33] hover:bg-amber-400 font-semibold h-11">
                  {creating ? "Cadastrando…" : "Cadastrar"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading && <div className="text-slate-400 py-10 text-center" data-testid="athletes-loading">Carregando atletas…</div>}
      {error && !loading && <div className="text-red-300 py-4">{error} <button onClick={load} className="underline">Tentar novamente</button></div>}
      {!loading && !error && !filtered.length && (
        <div className="text-slate-400 py-10 text-center border border-dashed border-white/10 rounded-lg" data-testid="athletes-empty">
          {q ? "Nenhum atleta corresponde à busca." : "Nenhum atleta cadastrado ainda."}
        </div>
      )}

      {!loading && !error && !!filtered.length && (
        <div className="grid md:grid-cols-2 gap-4">
          {filtered.map(a => {
            const src = avatarSrc(a);
            return (
              <Card key={a.id} className="bg-transparent gsx-card-dark p-5" data-testid={`athlete-row-${a.id}`}>
                <div className="flex justify-between items-start flex-wrap gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-12 h-12 rounded-full border border-amber-500/40 bg-amber-500/10 overflow-hidden flex items-center justify-center shrink-0">
                      {src ? (
                        <img src={src} alt={a.name} className="w-full h-full object-cover" data-testid={`athlete-avatar-${a.id}`} />
                      ) : (
                        <span className="text-amber-300 font-semibold text-lg">{(a.name||"?").slice(0,1)}</span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="text-white font-semibold truncate">{a.name}</div>
                      <div className="text-xs text-slate-400 truncate">{a.email} • {a.category || "—"}</div>
                      {a.plan_pdf_name && <div className="text-xs text-amber-300 mt-1 truncate">📄 {a.plan_pdf_name}</div>}
                    </div>
                  </div>
                  <div className="text-right text-xs text-slate-400">
                    <div>CBX <span className="font-mono text-amber-300">{a.rating_cbx || "—"}</span></div>
                    <div>FIDE <span className="font-mono text-amber-300">{a.rating_fide || "—"}</span></div>
                  </div>
                </div>
                <div className="mt-4 flex gap-2 flex-wrap">
                  <Button size="sm" onClick={()=>openEdit(a)} data-testid={`athlete-edit-${a.id}`}
                    className="bg-amber-500 text-[#071A33] hover:bg-amber-400 font-semibold">
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Editar Atleta
                  </Button>
                  {a.plan_pdf_name && (
                    <Button size="sm" variant="outline" onClick={()=>openPdfInNewTab(a.id)}
                      data-testid={`athlete-view-pdf-${a.id}`}
                      className="border-amber-500/30 text-amber-300 bg-transparent hover:bg-amber-500/10">
                      <Eye className="w-3.5 h-3.5 mr-1" /> Ver PDF
                    </Button>
                  )}
                  <AdminDeleteConfirmation itemName={a.name} onConfirm={() => remove(a)}><Button size="sm" variant="destructive" data-testid={`athlete-delete-${a.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button></AdminDeleteConfirmation>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!editAthlete} onOpenChange={(v)=>!v && setEditAthlete(null)}>
        <DialogContent className="bg-[#071A33] border-amber-500/30 text-white max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Editar — {editAthlete?.name}</DialogTitle></DialogHeader>
          {editAthlete && (
            <>
              {/* Foto de perfil */}
              <div className="pb-6 border-b border-white/10" data-testid="edit-avatar-section">
                <div className="flex items-center gap-2 mb-3">
                  <Camera className="w-4 h-4 text-amber-400" />
                  <h3 className="text-white font-semibold">Foto de perfil</h3>
                </div>
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="w-20 h-20 rounded-full border border-amber-500/40 bg-amber-500/10 overflow-hidden flex items-center justify-center shrink-0">
                    {avatarSrc(editAthlete) ? (
                      <img src={avatarSrc(editAthlete)} alt={editAthlete.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-amber-300 font-semibold text-2xl">{(editAthlete.name||"?").slice(0,1)}</span>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" onClick={()=>avatarInputRef.current?.click()} disabled={uploadingAvatar}
                      data-testid="upload-avatar-btn"
                      className="bg-amber-500 text-[#071A33] hover:bg-amber-400 font-semibold">
                      <FileUp className="w-3.5 h-3.5 mr-1" /> {uploadingAvatar ? "Enviando…" : editAthlete.avatar_file_id ? "Substituir foto" : "Enviar foto"}
                    </Button>
                    {editAthlete.avatar_file_id && (
                      <Button size="sm" variant="destructive" onClick={deleteAvatar} data-testid="delete-avatar-btn">
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Remover
                      </Button>
                    )}
                    <div className="text-xs text-slate-400 self-center">JPG/PNG/WEBP até 5 MB</div>
                  </div>
                  <input ref={avatarInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={uploadAvatar} className="hidden" data-testid="avatar-input" />
                </div>
              </div>

              <form onSubmit={submitEdit} className="grid sm:grid-cols-2 gap-4 py-6 border-b border-white/10" data-testid="athlete-edit-form">
                <F label="Nome completo" v={editForm.name} set={(v)=>setEF("name",v)} required />
                <F label="Email" type="email" v={editForm.email} set={(v)=>setEF("email",v)} testid="edit-athlete-email" required />
                <F label="Nova senha (opcional)" v={editForm.password} set={(v)=>setEF("password",v)} testid="edit-athlete-password" />
                <F label="Data de nascimento" type="date" v={editForm.birth_date} set={(v)=>setEF("birth_date",v)} />
                <F label="Categoria" v={editForm.category} set={(v)=>setEF("category",v)} testid="edit-athlete-category" />
                <F label="Turma (ex: Terça 19h)" v={editForm.class_name} set={(v)=>setEF("class_name",v)} testid="edit-athlete-class" />
                <F label="Escola" v={editForm.school} set={(v)=>setEF("school",v)} testid="edit-athlete-school" />
                <F label="Cidade" v={editForm.city} set={(v)=>setEF("city",v)} />
                <F label="CPF" type="tel" v={editForm.cpf} set={(v)=>setEF("cpf",v.replace(/\D/g, "").slice(0, 11))} testid="edit-athlete-cpf" />
                <F label="RG" v={editForm.rg} set={(v)=>setEF("rg",v)} testid="edit-athlete-rg" />
                <RatingEditor label="CBX" checked={editForm.has_rating_cbx} onCheckedChange={v=>setEF("has_rating_cbx",v)} value={editForm.rating_cbx} onValueChange={v=>setEF("rating_cbx",v)} testid="edit-athlete-rating-cbx" />
                <RatingEditor label="FIDE" checked={editForm.has_rating_fide} onCheckedChange={v=>setEF("has_rating_fide",v)} value={editForm.rating_fide} onValueChange={v=>setEF("rating_fide",v)} testid="edit-athlete-rating-fide" />
                <F label="Username Chess.com" v={editForm.chess_com_username} set={(v)=>setEF("chess_com_username",v)} />
                <F label="Username Lichess" v={editForm.lichess_username} set={(v)=>setEF("lichess_username",v)} />
                <F label="ID FSX" type="number" v={editForm.fsx_player_id} set={(v)=>setEF("fsx_player_id",v)} />
                <div className="sm:col-span-2 space-y-2">
                  <Label className="text-slate-300">Informações adicionais</Label>
                  <Textarea rows={3} value={editForm.additional_info||""}
                    onChange={(e)=>setEF("additional_info", e.target.value)}
                    data-testid="edit-athlete-additional-info"
                    className="bg-[#0D2A52] border-amber-500/20 text-white" />
                </div>
                <Button type="submit" disabled={editing} data-testid="edit-athlete-save"
                  className="sm:col-span-2 bg-amber-500 text-[#071A33] hover:bg-amber-400 font-semibold h-11">
                  {editing ? "Salvando…" : "Salvar Alterações"}
                </Button>
              </form>

              <div className="py-6 border-b border-white/10">
                <h3 className="text-white font-semibold mb-3">Ratings online</h3>
                <OnlineRatings
                  athleteId={editAthlete.id}
                  chessComUsername={editForm.chess_com_username}
                  lichessUsername={editForm.lichess_username}
                  fsxPlayerId={editForm.fsx_player_id}
                />
                <div className="grid sm:grid-cols-2 gap-3 mt-3" data-testid="manual-ratings">
                  <ManualRatingCard label="FIDE" value={editForm.rating_fide} testid="admin-rating-fide" />
                  <ManualRatingCard label="CBX" value={editForm.rating_cbx} testid="admin-rating-cbx" />
                </div>
              </div>

              {/* Plano de Treinamento */}
              <div className="py-6 border-b border-white/10" data-testid="edit-plan-section">
                <div className="flex items-center gap-2 mb-3">
                  <ClipboardList className="w-4 h-4 text-amber-400" />
                  <h3 className="text-white font-semibold">Plano de Treinamento</h3>
                </div>
                {editAthlete.plan_pdf_name ? (
                  <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-amber-500/30 bg-amber-500/5">
                    <div className="min-w-0">
                      <div className="text-white text-sm truncate">📄 {editAthlete.plan_pdf_name}</div>
                      <div className="text-xs text-slate-400">Publicado — visível no painel do atleta.</div>
                    </div>
                    <div className="flex gap-2 shrink-0 flex-wrap">
                      <Button size="sm" variant="outline" onClick={()=>openPdfInNewTab(editAthlete.id)}
                        data-testid="edit-view-pdf-btn"
                        className="border-amber-500/30 text-amber-300 bg-transparent hover:bg-amber-500/10">
                        <FileDown className="w-3.5 h-3.5 mr-1" /> Ver PDF
                      </Button>
                      <Button size="sm" onClick={()=>pdfInputRef.current?.click()} disabled={uploadingPdf}
                        data-testid="replace-plan-btn"
                        className="bg-amber-500 text-[#071A33] hover:bg-amber-400 font-semibold">
                        <FileUp className="w-3.5 h-3.5 mr-1" /> Substituir
                      </Button>
                      <Button size="sm" variant="destructive" onClick={deletePdf}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-dashed border-white/10">
                    <div className="text-sm text-slate-400">Nenhum plano publicado ainda.</div>
                    <Button size="sm" onClick={()=>pdfInputRef.current?.click()} disabled={uploadingPdf}
                      data-testid="upload-plan-btn"
                      className="bg-amber-500 text-[#071A33] hover:bg-amber-400 font-semibold">
                      <FileUp className="w-3.5 h-3.5 mr-1" /> {uploadingPdf ? "Enviando…" : "Anexar PDF"}
                    </Button>
                  </div>
                )}
                <input ref={pdfInputRef} type="file" accept="application/pdf" onChange={uploadPdf}
                  className="hidden" data-testid="plan-pdf-input" />
              </div>

              {/* Vídeos individuais */}
              <div className="pt-6" data-testid="edit-videos-section">
                <div className="flex items-center gap-2 mb-3">
                  <VideoIcon className="w-4 h-4 text-amber-400" />
                  <h3 className="text-white font-semibold">Vídeos individuais</h3>
                </div>
                <form onSubmit={saveVideo} className="grid sm:grid-cols-2 gap-3 mb-4" data-testid="video-form">
                  <F label="Título" v={videoForm.title} set={(v)=>setVideoForm(f=>({...f,title:v}))} testid="video-title-input" required />
                  <F label="Link do YouTube" v={videoForm.youtube_url} set={(v)=>setVideoForm(f=>({...f,youtube_url:v}))} testid="video-url-input" required />
                  <div className="space-y-2">
                    <Label className="text-slate-300">Categoria</Label>
                    <Select value={videoForm.category} onValueChange={(v)=>setVideoForm(f=>({...f,category:v}))}>
                      <SelectTrigger data-testid="video-category-select" className="bg-[#0D2A52] border-amber-500/20 text-white">
                        <SelectValue placeholder="Categoria" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#071A33] text-white border-amber-500/20">
                        {VIDEO_CATEGORIES.map(c => (
                          <SelectItem key={c} value={c} data-testid={`video-cat-${c}`}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Descrição</Label>
                    <Textarea rows={1} value={videoForm.description}
                      onChange={(e)=>setVideoForm(f=>({...f,description:e.target.value}))}
                      className="bg-[#0D2A52] border-amber-500/20 text-white" />
                  </div>
                  <div className="sm:col-span-2 flex gap-2">
                    <Button type="submit" data-testid="video-save-btn"
                      className="bg-amber-500 text-[#071A33] hover:bg-amber-400 font-semibold flex-1">
                      {editingVideoId ? "Salvar alterações" : "Adicionar vídeo"}
                    </Button>
                    {editingVideoId && (
                      <Button type="button" variant="outline" onClick={()=>{setEditingVideoId(null); setVideoForm(emptyVideo);}}
                        className="border-amber-500/30 text-amber-300 bg-transparent hover:bg-amber-500/10">Cancelar</Button>
                    )}
                  </div>
                </form>
                <div className="space-y-2">
                  {!videos.length && <div className="text-sm text-slate-400 py-4 text-center border border-dashed border-white/10 rounded-lg">Nenhum vídeo ainda.</div>}
                  {videos.map(v => (
                    <div key={v.id} className="flex justify-between items-start gap-3 p-3 rounded-lg border border-white/10 bg-[#0D2A52]/40" data-testid={`video-item-${v.id}`}>
                      <div className="min-w-0">
                        <div className="text-white font-medium truncate flex items-center gap-2">
                          {v.title}
                          <span className="text-[10px] uppercase tracking-widest bg-amber-500/15 text-amber-300 border border-amber-500/30 px-1.5 py-0.5 rounded">{v.category || "Outros"}</span>
                        </div>
                        <a href={v.youtube_url} target="_blank" rel="noreferrer" className="text-xs text-amber-400 hover:text-amber-300 truncate block">{v.youtube_url}</a>
                        {v.description && <div className="text-xs text-slate-400 mt-1">{v.description}</div>}
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button size="icon" variant="ghost" onClick={()=>editVideo(v)} className="border border-amber-400/70 bg-amber-500/15 text-amber-200 hover:bg-amber-400 hover:text-[#071A33] hover:border-amber-300 h-8 w-8">
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={()=>removeVideo(v)} className="text-red-400 hover:bg-red-500/10 h-8 w-8">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

function ManualRatingCard({ label, value, testid }) {
  return <div className="rounded-lg border border-amber-500/20 bg-[#0D2A52]/45 p-3" data-testid={testid}>
    <div className="text-sm text-white font-semibold">{label}</div>
    <div className={value == null || value === "" ? "text-sm text-slate-500 mt-3" : "font-mono text-lg text-amber-300 mt-3"}>
      {value == null || value === "" ? "Não informado" : value}
    </div>
  </div>;
}

function RatingEditor({ label, checked, onCheckedChange, value, onValueChange, testid }) {
  const id = `${testid}-has-rating`;
  return <div className={`rounded-xl border p-4 transition-colors ${checked ? "border-amber-300/30 bg-gradient-to-br from-amber-300/[0.07] to-transparent" : "border-white/[0.08] bg-white/[0.02]"}`}>
    <div className="flex items-center justify-between gap-3">
      <label htmlFor={id} className="flex cursor-pointer items-center gap-3 text-sm font-medium text-slate-200">
        <Checkbox id={id} checked={Boolean(checked)} onCheckedChange={onCheckedChange} data-testid={`${testid}-checkbox`} className="h-5 w-5 rounded-md border-slate-500 data-[state=checked]:border-amber-300 data-[state=checked]:bg-amber-300 data-[state=checked]:text-[#071A33]" />
        Possui rating {label}
      </label>
      <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${checked ? "bg-amber-300/10 text-amber-200" : "bg-white/[0.05] text-slate-500"}`}>{checked ? "Ativo" : "Não possui"}</span>
    </div>
    {checked && <div className="mt-4 border-t border-white/[0.08] pt-4"><F label={`Rating ${label}`} type="number" v={value} set={onValueChange} testid={testid} /></div>}
  </div>;
}

function F({ label, v, set, type = "text", required, testid, minLength, maxLength, pattern }) {
  return (
    <div className="space-y-2">
      <Label className="text-slate-300">{label}</Label>
      <Input type={type} value={v ?? ""} required={required} minLength={minLength} maxLength={maxLength} pattern={pattern} data-testid={testid}
        onChange={(e)=>set(e.target.value)}
        className="bg-[#0D2A52] border-amber-500/20 text-white" />
    </div>
  );
}
